<script setup>
import AppBadge from '@/components/atoms/AppBadge'
import AppButton from '@/components/atoms/AppButton'
import AppCard from '@/components/molecules/AppCard'
import GetStartedStepIllustration from '@/components/GetStarted/GetStartedStepIllustration.vue'
import HelpHubStepMarker from './HelpHubStepMarker.vue'

defineProps({
  number: { type: Number, required: true },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  // The step's key picks its picture, the same scene as its Home card.
  stepKey: { type: String, default: '' },
  // The photo a step without a scene shows.
  image: { type: String, default: '' },
  to: { type: [String, Object], required: true },
  active: Boolean,
  locked: Boolean,
  // A finished step is a one-line row: a check for its number, no picture.
  completed: Boolean,
  // The step can be marked complete by hand: a badge over its picture.
  completable: Boolean,
  // The mark is being written.
  completing: Boolean,
})

const emit = defineEmits(['complete'])
</script>

<template>
  <AppCard as="article" size="sm" :to="to" :title="title" :highlight="active" hoverable reverse>
    <div v-if="!completed" class="relative">
      <!-- Figma keeps the height as the hub widens and crops the picture instead. -->
      <GetStartedStepIllustration :step-key="stepKey" :image="image" class="h-36 w-full" />

      <AppBadge size="xs" custom-class="bg-black/60 text-white" class="absolute bottom-2 left-2">
        Watch
      </AppBadge>

      <!-- Over AppCard's link, which covers the card (z-10, as HelpHubTip lifts its own link); .stop keeps the card's click tracking out of it. -->
      <AppButton
        v-if="completable"
        variant="soft-outlined"
        size="xs"
        rounded="full"
        leading-icon="tabler:circle"
        icon-class="text-blue-300"
        class="absolute right-3 top-3 z-10 gap-[5px]"
        :loading="completing"
        :aria-label="`Mark ${title} as complete`"
        @click.stop="emit('complete')"
      >
        Mark as complete
      </AppButton>
    </div>

    <template #leading>
      <!-- Figma centres the check on the row's two lines; the number sits on the title's line. -->
      <span v-if="completed" class="flex h-full items-center">
        <HelpHubStepMarker variant="completed" />
      </span>

      <HelpHubStepMarker v-else :variant="active ? 'active' : 'outline'">{{ number }}</HelpHubStepMarker>
    </template>

    <template #title>
      <h4 v-text="title" />

      <AppBadge v-if="locked" size="xs" variant="tint-light" role="img" aria-label="Locked">
        <Icon icon="heroicons-outline:lock-closed" class="size-4" />
      </AppBadge>
    </template>

    <template #description>
      <p :class="completed ? 'truncate' : 'line-clamp-2'" v-text="description" />
    </template>
  </AppCard>
</template>
